import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import type { User, AuthContextType, LoginCredentials } from '@/types'
import { authService } from '@services/authService'
import toast from 'react-hot-toast'

// Auth State
interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
}

// Auth Actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }

// Auth Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      }
    default:
      return state
  }
}

// Initial State
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token')
      const refreshToken = localStorage.getItem('refreshToken')
      
      if (token && refreshToken) {
        try {
          const user = await authService.getCurrentUser()
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } })
        } catch (error: any) {
          console.error('Token verification failed:', error)
          
          // Check if it's a token expiration error
          if (error.message && error.message.includes('401')) {
            // Try to refresh token
            try {
              const refreshResponse = await authService.refreshToken()
              localStorage.setItem('token', refreshResponse.accessToken)
              localStorage.setItem('refreshToken', refreshResponse.refreshToken)
              
              // Retry getting user info with new token
              const user = await authService.getCurrentUser()
              dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: refreshResponse.accessToken } })
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError)
              // Both token and refresh failed, clear everything
              localStorage.removeItem('token')
              localStorage.removeItem('refreshToken')
              dispatch({ type: 'LOGOUT' })
            }
          } else {
            // Token is invalid, remove both tokens
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            dispatch({ type: 'LOGOUT' })
          }
        }
      } else {
        // If either token is missing, clear both and set loading to false
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    initializeAuth()
  }, [])

  // Login function
  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const response = await authService.login(credentials)
      
      // Store both tokens in localStorage
      localStorage.setItem('token', response.accessToken)
      localStorage.setItem('refreshToken', response.refreshToken)
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, token: response.accessToken },
      })
      
      toast.success(`歡迎回來，${response.user.name}！`)
    } catch (error: any) {
      dispatch({ type: 'SET_LOADING', payload: false })
      const message = error.response?.data?.message || '登入失敗，請檢查您的帳號密碼'
      toast.error(message)
      throw error
    }
  }

  // Logout function
  const logout = (): void => {
    try {
      // Remove both tokens from localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      
      // Clear auth state
      dispatch({ type: 'LOGOUT' })
      
      toast.success('已成功登出')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Update user function - 只更新本地狀態，不調用API
  const updateUser = (userData: User): void => {
    dispatch({ type: 'UPDATE_USER', payload: userData })
  }

  // Context value
  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    login,
    logout,
    updateUser,
    isLoading: state.isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// HOC for components that require authentication
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { user, isLoading } = useAuth()

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-secondary-600 font-medium">載入中...</p>
          </div>
        </div>
      )
    }

    if (!user) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-secondary-800 mb-2">需要登入</h2>
            <p className="text-secondary-600">請先登入以使用此功能</p>
          </div>
        </div>
      )
    }

    return <Component {...props} />
  }
}

export default AuthContext
